import { useRef, useEffect, useState } from 'react';
import { Handle, Position, NodeProps, useReactFlow } from 'reactflow';
import { Button } from '../../../../../components/ui/button';
import { Card } from '../../../../../components/ui/card';
import { Badge } from '../../../../../components/ui/badge';
import { motion } from 'framer-motion';
import { useExpandable } from '../../../../../hooks/use-expandable';
import { MessageSquare, ChevronDown, ChevronUp, AlertCircle, Loader2, CheckCircle, AlertTriangle, Trash2 } from 'lucide-react';
import { messagesApi } from '../../../services/messagesApi';
import { toast } from '@/hooks/use-toast';
import { AddNodeMenu } from './AddNodeMenu';

interface TextMessageData {
  label?: string;
  message?: string;
  messageId?: string;
  processingStatus?: 'pending' | 'processing' | 'completed' | 'error';
  flowId?: string;
  groupId?: string;
  sequenceOrder?: number;
}

export function TextMessageNode({ id, data, isConnectable }: NodeProps<TextMessageData>) {
  const contentRef = useRef<HTMLDivElement>(null);
  const { isExpanded, toggleExpand, animatedHeight } = useExpandable(false);
  const { setNodes, getNodes } = useReactFlow();
  const [message, setMessage] = useState(data.message || '');
  const [hasQuotes, setHasQuotes] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [processingStatus, setProcessingStatus] = useState<'pending' | 'processing' | 'completed' | 'error'>(
    data.processingStatus || 'pending'
  );

  useEffect(() => {
    if (contentRef.current) {
      animatedHeight.set(isExpanded ? contentRef.current.offsetHeight : 0);
    }
  }, [isExpanded, animatedHeight]);

  const handleMessageChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newMessage = e.target.value;
    setMessage(newMessage);

    // Detectar comillas simples o dobles
    const hasQuotesDetected = /["']/.test(newMessage);
    setHasQuotes(hasQuotesDetected);
  };

  const handleSave = async () => {
    if (!message || hasQuotes) {
      toast.error('Debes escribir un mensaje válido');
      return;
    }

    setIsSaving(true);
    setProcessingStatus('processing');

    try {
      // Obtener valores actualizados del nodo en el momento del guardado
      const currentNodes = getNodes();
      const currentNode = currentNodes.find(n => n.id === id);

      if (!currentNode) {
        throw new Error('Nodo no encontrado');
      }

      const savedMessage = await messagesApi.upsertMessage({
        id: data.messageId,
        node_id: id,
        node_type: 'text',
        text_content: message,
        ai_processing_status: 'completed',
        flow_id: currentNode.data.flowId || null,
        group_id: currentNode.data.groupId || null,
        sequence_order: currentNode.data.sequenceOrder || 1,
      });

      // Actualizar el nodo en ReactFlow
      setNodes((nodes) =>
        nodes.map((node) =>
          node.id === id
            ? {
              ...node,
              data: {
                ...node.data,
                message: message,
                messageId: savedMessage.id,
                processingStatus: 'completed'
              }
            }
            : node
        )
      );

      setProcessingStatus('completed');
      toast.success(data.messageId ? 'Mensaje actualizado correctamente' : 'Mensaje guardado correctamente');
    } catch (error) {
      console.error('Error:', error);
      setProcessingStatus('error');
      toast.error('Error al guardar el mensaje');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!data.messageId) {
      toast.error('No hay mensaje guardado para eliminar');
      return;
    }

    if (!window.confirm('¿Estás seguro de eliminar este mensaje de la base de datos?')) {
      return;
    }

    try {
      await messagesApi.deleteMessage(id);

      // Actualizar el nodo en ReactFlow
      setNodes((nodes) =>
        nodes.map((node) =>
          node.id === id
            ? {
              ...node,
              data: {
                ...node.data,
                messageId: undefined,
                processingStatus: 'pending'
              }
            }
            : node
        )
      );

      toast.success('Mensaje eliminado de la base de datos');
    } catch (error) {
      console.error('Error eliminando mensaje:', error);
      toast.error('Error al eliminar el mensaje');
    }
  };

  const getStatusBadge = () => {
    if (isSaving) {
      return (
        <Badge className="bg-blue-500">
          <Loader2 className="h-3 w-3 animate-spin" />
        </Badge>
      );
    }
    if (processingStatus === 'completed' && data.messageId) {
      return (
        <Badge className="bg-green-500">
          <CheckCircle className="h-3 w-3" />
        </Badge>
      );
    }
    if (processingStatus === 'error') {
      return (
        <Badge className="bg-red-500">
          <AlertTriangle className="h-3 w-3" />
        </Badge>
      );
    }
    return (
      <Badge className="bg-yellow-500">
        <AlertCircle className="h-3 w-3" />
      </Badge>
    );
  };

  return (
    <Card className="min-w-[300px] max-w-[400px] bg-white shadow-lg border-2 border-primary/20">
      {/* Número de secuencia - arriba izquierda */}
      {data.groupId && (
        <div className="absolute -top-3 -left-3 z-10">
          <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-bold shadow-lg border-2 border-white">
            {data.sequenceOrder || 1}
          </div>
        </div>
      )}

      <div className="absolute -top-2 -right-2 z-10">
        {getStatusBadge()}
      </div>

      <Handle
        type="target"
        position={Position.Left}
        isConnectable={isConnectable}
        className="w-5 h-5 !bg-primary hover:w-6 hover:h-6 transition-all cursor-crosshair"
        style={{ border: '3px solid white' }}
      />

      <div className="p-4">
        <div className="flex justify-between items-center gap-2 mb-2">
          <div className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5 text-primary" />
            <h3 className="font-medium text-foreground">Mensaje de Texto</h3>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={toggleExpand}
            className="h-8 w-8 p-0"
          >
            {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </Button>
        </div>

        <motion.div
          style={{ height: animatedHeight }}
          className="overflow-hidden"
        >
          <div ref={contentRef} className="space-y-3 pt-2">
            <div>
              <label className="text-xs text-gray-600 mb-1 block">
                Mensaje a enviar:
              </label>
              <textarea
                value={message}
                onChange={handleMessageChange}
                placeholder="Escribe el mensaje que se enviará por WhatsApp..."
                className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 resize-none ${hasQuotes
                  ? 'border-red-500 focus:ring-red-500 bg-red-50'
                  : 'border-gray-300 focus:ring-primary'
                  }`}
                rows={4}
              />

              {hasQuotes && (
                <div className="flex items-start gap-2 mt-2 p-2 bg-red-50 border border-red-200 rounded-lg">
                  <AlertCircle className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-red-700">
                    Las comillas no están permitidas en los flujos de mensaje.
                  </p>
                </div>
              )}
            </div>

            <div className="flex items-center justify-between text-xs text-gray-500">
              <span>{message.length} caracteres</span>
              <div className="flex items-center gap-2">
                {data.messageId && (
                  <Button
                    size="sm"
                    variant="destructive"
                    className="h-7 w-7 p-0"
                    onClick={handleDelete}
                    disabled={isSaving}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                )}
                <Button
                  size="sm"
                  className="h-7 text-xs"
                  disabled={hasQuotes || !message || isSaving}
                  onClick={handleSave}
                >
                  {isSaving ? (
                    <>
                      <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                      Guardando...
                    </>
                  ) : (
                    data.messageId ? 'Actualizar' : 'Guardar'
                  )}
                </Button>
              </div>
            </div>
          </div>
        </motion.div>

        {!isExpanded && message && (
          <div className="mt-2">
            {hasQuotes ? (
              <div className="flex items-center gap-1 text-xs text-red-600">
                <AlertCircle className="h-3 w-3" />
                <span>Contiene comillas - Debes corregir</span>
              </div>
            ) : (
              <div className="text-xs text-gray-500 truncate">
                {message}
              </div>
            )}
          </div>
        )}
      </div>

      <Handle
        type="source"
        position={Position.Right}
        isConnectable={isConnectable}
        className="w-5 h-5 !bg-primary hover:w-6 hover:h-6 transition-all cursor-crosshair"
        style={{ border: '3px solid white' }}
      />

      <AddNodeMenu nodeId={id} />
    </Card>
  );
}
